import { useEffect, useRef, useState } from 'react';
import { useTracks, useRemoteParticipants, useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';
import TranscriptLines from './TranscriptLines';
import './AvatarPanel.css';

export default function AvatarPanel({ transcript, hideUserPip }) {
  const remoteParticipants = useRemoteParticipants();
  const { localParticipant } = useLocalParticipant();
  const agentParticipant = remoteParticipants.find(
    (p) => p.kind === 4 || p.identity?.includes('agent')
  );

  const tracks = useTracks([Track.Source.Camera], { onlySubscribed: true });
  const agentVideoTrack = tracks.find(
    (t) => t.source === Track.Source.Camera && t.participant && (
      t.participant.identity === agentParticipant?.identity ||
      t.participant.kind === 4 ||
      t.participant.identity?.includes('agent') ||
      t.participant.identity?.includes('avatar')
    )
  );

  const videoRef = useRef(null);
  const userVideoRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const attachedTrackRef = useRef(null);

  // Transcript is already agent-only (filtered in App.js)
  const agentTranscript = transcript;

  // Attach agent video track. Re-run only when the track sid changes
  // (a new track post-avatar-swap). Body always detaches the previously
  // attached track before attaching the new one so a brief null-track
  // window doesn't leave the old MediaStream bound to <video>.
  const trackSid = agentVideoTrack?.publication?.track?.sid;
  useEffect(() => {
    const el = videoRef.current;
    const track = agentVideoTrack?.publication?.track;
    const prev = attachedTrackRef.current;
    if (prev && prev !== track) {
      try { prev.detach(el); } catch (e) { /* ignore */ }
      attachedTrackRef.current = null;
      setHasVideo(false);
    }
    if (!el || !track) { setHasVideo(false); return; }
    if (attachedTrackRef.current === track) return;
    track.attach(el);
    attachedTrackRef.current = track;
    setHasVideo(true);
    return () => {
      if (attachedTrackRef.current === track) {
        try { track.detach(el); } catch (e) { /* ignore */ }
        attachedTrackRef.current = null;
        setHasVideo(false);
      }
    };
  }, [trackSid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach local user camera for PiP
  useEffect(() => {
    const el = userVideoRef.current;
    if (!el || !localParticipant) return;
    const attachCam = () => {
      const camPub = localParticipant.getTrackPublication(Track.Source.Camera);
      if (camPub?.track) camPub.track.attach(el);
    };
    attachCam();
    localParticipant.on('trackPublished', attachCam);
    localParticipant.on('localTrackPublished', attachCam);
    return () => {
      localParticipant.off('trackPublished', attachCam);
      localParticipant.off('localTrackPublished', attachCam);
      const camPub = localParticipant.getTrackPublication(Track.Source.Camera);
      if (camPub?.track) camPub.track.detach(el);
    };
  }, [localParticipant]);

  // Monitor agent speaking state
  useEffect(() => {
    if (!agentParticipant) return;
    const handler = (speaking) => setIsSpeaking(speaking);
    agentParticipant.on('isSpeakingChanged', handler);
    return () => { agentParticipant.off('isSpeakingChanged', handler); };
  }, [agentParticipant]);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [agentTranscript]);

  return (
    <>
      {/* Avatar with pulse rings — half size */}
      <div className="avatar-section">
        <div className={`avatar-rings ${isSpeaking ? 'speaking' : ''}`}>
          <div className="ring ring-outer" />
          <div className="ring ring-mid" />
          <div className="ring ring-inner" />
          <div className="avatar-frame">
            <div className="avatar-frame-inner">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="avatar-video"
                style={{ display: hasVideo ? 'block' : 'none' }}
              />
              {!hasVideo && (
                <div className="avatar-placeholder">
                  <svg width="48" height="48" viewBox="0 0 80 80" fill="none">
                    <circle cx="40" cy="30" r="16" stroke="#2c8cc9" strokeWidth="2.5" fill="none" />
                    <path d="M12 68c0-15.464 12.536-28 28-28s28 12.536 28 28" stroke="#2c8cc9" strokeWidth="2.5" fill="none" />
                  </svg>
                  <p>{agentParticipant ? 'Loading...' : 'Waiting...'}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Name badge */}
        <div className="name-badge">
          <div className="audio-bars">
            <span className={`ab ${isSpeaking ? 'active' : ''}`} />
            <span className={`ab ${isSpeaking ? 'active' : ''}`} />
            <span className={`ab ${isSpeaking ? 'active' : ''}`} />
            <span className={`ab ${isSpeaking ? 'active' : ''}`} />
          </div>
          <span className="name-text">Eva</span>
        </div>
      </div>

      {/* Agent-only transcript */}
      <div className="transcript-panel">
        <div className="transcript-scroll">
          <TranscriptLines transcript={agentTranscript} />
          <div ref={transcriptEndRef} />
        </div>
      </div>

      {/* User self-view PiP — kept mounted but hidden when proctor is active */}
      <div
        className="user-video-pip"
        style={hideUserPip ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
      >
        <video ref={userVideoRef} autoPlay playsInline muted />
        <span className="pip-label">You</span>
      </div>
    </>
  );
}
